import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { SessizDugme } from '../lib/dugme';
import { useOturum } from '../lib/oturum';
import type { DuzeltmeAlani } from '@mod/domain';

/**
 * ADIM DUZELTME — bitmis bir adimda girilen degerleri duzeltmek.
 *
 * ⚠️ Bu bir "duzenle" DEGIL, EK KAYITTIR. Eski deger silinmez; iz kaydinda
 *    "eski → yeni" olarak durur (T4 append-only). Gerekce ZORUNLU: gerekcesiz
 *    bir duzeltme denetimde HATADAN ayirt edilemez.
 *
 * Yetki `duzeltilebilirMi` (packages/domain) ile belirlenir ve iki tarafta da
 * ayni fonksiyondan gelir — bu bilesen yalnizca formu cizer, kapiyi TUTMAZ.
 * Sunucu istegi bagimsiz olarak yeniden dogrular.
 *
 * ⚠️ IKI EKRAN DA BUNU KULLANIR (masaustu IsDetay + telefon SahaIsDetay).
 *    Formen saha rolu; duzeltme yalniz masaustunde olsaydi asil kullanicisina
 *    hic ulasmazdi.
 */
export interface DuzeltmeIsi {
  id: string;
  isEmriNo: string | null;
  tanim: string;
  /** Ünite düzeltmesi kimlikle çalışır; ekrandaki ad yalnız gösterim. */
  uniteId?: string | null;
  unite?: string;
  /** SAP iş emri alanları — yalnız `sapAlanlari` açık müşteride düzeltilir. */
  sorumluIsyeri?: string | null;
  planlamaGrubu?: string | null;
  oncelik?: number | null;
  durum: string;
  iskeleOnayNo: string | null;
  kesifNot: string | null;
  izin: 'alindi' | 'override' | null;
  izinNotu: string | null;
  malzemeSevk: boolean;
  olculer: { ad: string | null; en: number | null; boy: number | null; yuk: number | null }[];
  fazlar: { tip: string; ekipIds?: string[]; ekipBasiId?: string | null }[];
}

export function AdimDuzelt({
  d,
  adim,
  onKapat,
  onBitti,
}: {
  d: DuzeltmeIsi;
  adim: DuzeltmeAlani;
  onKapat: () => void;
  onBitti: () => void;
}) {
  const [gerekce, setGerekce] = useState('');
  const [isEmriNo, setIsEmriNo] = useState(d.isEmriNo ?? '');
  const [tanim, setTanim] = useState(d.tanim);
  const [uniteId, setUniteId] = useState(d.uniteId ?? '');
  const { ben } = useOturum();
  const sap = !!ben?.santiye.sapAlanlari;
  const [sorumluIsyeri, setSorumluIsyeri] = useState(d.sorumluIsyeri ?? '');
  const [planlamaGrubu, setPlanlamaGrubu] = useState(d.planlamaGrubu ?? '');
  const [oncelik, setOncelik] = useState(d.oncelik != null ? String(d.oncelik) : '');
  const [uniteler, setUniteler] = useState<{ id: string; ad: string; aktif: boolean }[]>([]);
  const [kesifNot, setKesifNot] = useState(d.kesifNot ?? '');
  const [olculer, setOlculer] = useState(
    d.olculer.map((o) => ({ ad: o.ad ?? '', en: String(o.en ?? ''), boy: String(o.boy ?? ''), yuk: o.yuk ? String(o.yuk) : '' })),
  );
  const [izin, setIzin] = useState<string>(d.izin ?? '');
  const [izinNotu, setIzinNotu] = useState(d.izinNotu ?? '');
  const [malzeme, setMalzeme] = useState(d.malzemeSevk);
  const kurulumFazi = d.fazlar.find((f) => f.tip === 'kurulum');
  const [ekip, setEkip] = useState<string[]>(kurulumFazi?.ekipIds ?? []);
  const [ekipBasi, setEkipBasi] = useState<string | null>(kurulumFazi?.ekipBasiId ?? null);
  const [calisanlar, setCalisanlar] = useState<{ id: string; ad: string; rol: string }[]>([]);
  const [onayNo, setOnayNo] = useState(d.iskeleOnayNo ?? '');
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  const sayi = (v: string) => Number(v.replace(',', '.'));

  /*
   * ⚠️ İŞİN MEVCUT ÜNİTESİ PASİFSE listede olmaz (`/tanimlar` yalnız aktifleri
   *    döner) ve seçici sessizce BAŞKA bir üniteyi gösterirdi. Kullanıcı hiçbir
   *    şeye dokunmadan kaydetse yanlış bölge yazılırdı — düzeltme aracının
   *    kendisi bozulma kaynağı olurdu. O yüzden mevcut değer her zaman listede.
   */
  const uniteSecenekleri =
    d.uniteId && !uniteler.some((u) => u.id === d.uniteId)
      ? [{ id: d.uniteId, ad: d.unite ?? 'mevcut ünite', aktif: false }, ...uniteler]
      : uniteler;

  useEffect(() => {
    if (adim !== 'ekip') return;
    void api
      .get<{ calisanlar: { id: string; ad: string; rol: string; sicilNo: number }[] }>('/tanimlar')
      .then((r) => setCalisanlar(r.calisanlar.filter((c) => c.sicilNo < 9000)))
      .catch(() => setCalisanlar([]));
  }, [adim]);

  /*
   * ÜNİTE LİSTESİ — künye düzeltmesinde gerekiyor.
   *
   * ⚠️ YALNIZ AKTİF ÜNİTELER seçilebilir; sunucu da pasifi reddediyor (K9).
   *    İşin ŞU ANKİ ünitesi pasife alınmışsa listede görünmez ama seçili
   *    değeri kaybetmemek için başa eklenir — aksi hâlde form açılır açılmaz
   *    sessizce başka bir üniteye işaret ederdi.
   */
  useEffect(() => {
    if (adim !== 'isEmri') return;
    void api
      .get<{ uniteler: { id: string; ad: string; aktif: boolean }[] }>('/tanimlar')
      .then((r) => setUniteler(r.uniteler))
      .catch(() => setUniteler([]));
  }, [adim]);

  async function kaydet() {
    setMesgul(true);
    setHata(null);
    try {
      const govde: Record<string, unknown> = {
        clientCommandId: crypto.randomUUID(),
        olayZamani: new Date().toISOString(),
        gerekce,
      };
      if (adim === 'isEmri') {
        govde.isEmriNo = isEmriNo.trim() || null;
        govde.tanim = tanim;
        if (sap) {
          govde.sorumluIsyeri = sorumluIsyeri.trim() || null;
          govde.planlamaGrubu = planlamaGrubu.trim() || null;
          govde.oncelik = oncelik ? Number(oncelik) : null;
        }
        // Değişmediyse GÖNDERİLMEZ: iz kaydına "ünite: X → X" satırı düşmesin.
        if (uniteId && uniteId !== (d.uniteId ?? '')) govde.uniteId = uniteId;
      } else if (adim === 'kesif') {
        govde.kesifNot = kesifNot.trim() || null;
        govde.olculer = olculer
          .filter((o) => o.en && o.boy)
          .map((o) => ({
            ad: o.ad.trim() || undefined,
            en: sayi(o.en),
            boy: sayi(o.boy),
            yuk: o.yuk ? sayi(o.yuk) : undefined,
          }));
      } else if (adim === 'izinMalzeme') {
        govde.izin = izin || null;
        govde.izinNotu = izinNotu.trim() || null;
        govde.malzemeSevk = malzeme;
      } else if (adim === 'ekip') {
        govde.ekip = ekip;
        govde.ekipBasiId = ekipBasi;
      } else {
        govde.iskeleOnayNo = onayNo.trim() || null;
        govde.nihaiOlculer = olculer
          .filter((o) => o.en && o.boy)
          .map((o) => ({
            ad: o.ad.trim() || undefined,
            en: sayi(o.en),
            boy: sayi(o.boy),
            yuk: o.yuk ? sayi(o.yuk) : undefined,
          }));
      }
      await api.patch(`/isler/${d.id}/duzelt`, govde);
      onBitti();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Kaydedilemedi');
      setMesgul(false);
    }
  }

  const gir =
    'w-full rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm outline-none focus:border-navy';
  const BASLIKLAR: Record<DuzeltmeAlani, string> = {
    // Başlıkta ÜNİTE de yazıyor: kullanıcı bölgeyi düzeltmek için hangi adımın
    // menüsüne bakacağını bilmiyordu ("düzeltme seçeneğini göremedim").
    isEmri: 'İş emri künyesi ve ünite',
    kesif: 'Keşif',
    izinMalzeme: 'İzin & malzeme',
    ekip: 'Kurulum ekibi',
    nihaiOlcu: 'Nihai ölçü & onay no',
  };
  const baslik = BASLIKLAR[adim];

  return (
    <div className="mb-4 rounded-(--radius-mod) border border-navy/30 bg-navy/5 p-4">
      <h3 className="mb-1 text-sm font-semibold text-navy">Düzeltme — {baslik}</h3>
      <p className="mb-3 text-xs leading-relaxed text-wait">
        Eski değer <strong>silinmez</strong>; iz kaydına “eski → yeni” olarak yazılır.
        Gerekçe zorunludur.
      </p>

      {adim === 'isEmri' && (
        <div className="mb-3 space-y-2">
          <input
            className={`${gir} num`}
            value={isEmriNo}
            placeholder="İş emri no (boş kalabilir)"
            onChange={(e) => setIsEmriNo(e.target.value)}
          />
          <input className={gir} value={tanim} onChange={(e) => setTanim(e.target.value)} />
          {/*
            ⚠️ ÜNİTE (bölge) — yanlış tuşlanan bölgenin tek düzeltme yolu
               (06.09.2026, Akçansa: "fırın 1 yerine atık ısı bölgesini
               seçmişim, düzeltme seçeneğini göremedim"). Ünite günlük rapora
               ve yeşil kart mailine yazıldığı için yanlış kalması müşteriye
               giden belgeye yansır.
          */}
          <label className="block text-xs text-wait">
            İskele kurulum bölgesi (ünite)
            <select
              className={`${gir} mt-1`}
              value={uniteId}
              onChange={(e) => setUniteId(e.target.value)}
            >
              {uniteler.length === 0 && <option value={uniteId}>{d.unite ?? 'yükleniyor…'}</option>}
              {uniteSecenekleri.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.ad}
                  {u.aktif ? '' : ' (pasif)'}
                </option>
              ))}
            </select>
          </label>
          {sap && (
            <div className="grid grid-cols-3 gap-2">
              <label className="block text-xs text-wait">
                Sorumlu işyeri
                <input
                  className={`${gir} num mt-1`}
                  maxLength={40}
                  value={sorumluIsyeri}
                  onChange={(e) => setSorumluIsyeri(e.target.value)}
                />
              </label>
              <label className="block text-xs text-wait">
                Planlama grubu
                <input
                  className={`${gir} num mt-1`}
                  maxLength={40}
                  value={planlamaGrubu}
                  onChange={(e) => setPlanlamaGrubu(e.target.value)}
                />
              </label>
              <label className="block text-xs text-wait">
                Öncelik
                <select
                  className={`${gir} mt-1`}
                  value={oncelik}
                  onChange={(e) => setOncelik(e.target.value)}
                >
                  <option value="">—</option>
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>
      )}

      {adim === 'kesif' && (
        <div className="mb-3 space-y-2">
          {olculer.map((o, i) => (
            <div key={i} className="flex flex-wrap items-center gap-1.5">
              <input
                className={`${gir} min-w-28 flex-1`}
                value={o.ad}
                placeholder="ad (ör. ön platform)"
                onChange={(e) =>
                  setOlculer(olculer.map((x, j) => (j === i ? { ...x, ad: e.target.value } : x)))
                }
              />
              {(['en', 'boy', 'yuk'] as const).map((alan) => (
                <input
                  key={alan}
                  className={`${gir} num w-20`}
                  inputMode="decimal"
                  value={o[alan]}
                  placeholder={alan}
                  onChange={(e) =>
                    setOlculer(olculer.map((x, j) => (j === i ? { ...x, [alan]: e.target.value } : x)))
                  }
                />
              ))}
              <button
                onClick={() => setOlculer(olculer.filter((_, j) => j !== i))}
                className="px-1.5 text-sm text-wait hover:text-crit"
              >
                ✕
              </button>
            </div>
          ))}
          <button
            onClick={() => setOlculer([...olculer, { ad: '', en: '', boy: '', yuk: '' }])}
            className="text-xs text-navy underline"
          >
            + ölçü satırı
          </button>
          <textarea
            className={`${gir} min-h-16`}
            value={kesifNot}
            placeholder="Keşif notu"
            onChange={(e) => setKesifNot(e.target.value)}
          />
          <p className="text-xs text-wait">
            Yükseklik girilmeyen satır <strong>alan (m²)</strong> sayılır — birim otomatik.
          </p>
        </div>
      )}

      {adim === 'izinMalzeme' && (
        <div className="mb-3 space-y-2 text-sm">
          <select className={gir} value={izin} onChange={(e) => setIzin(e.target.value)}>
            <option value="">İzin yok / girilmedi</option>
            <option value="alindi">İzin alındı</option>
            <option value="override">İzinsiz başlatıldı (override)</option>
          </select>
          {izin === 'override' && (
            <input
              className={gir}
              value={izinNotu}
              placeholder="Override gerekçesi"
              onChange={(e) => setIzinNotu(e.target.value)}
            />
          )}
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={malzeme} onChange={(e) => setMalzeme(e.target.checked)} />
            Malzeme hazır ve sevk edildi
          </label>
        </div>
      )}

      {adim === 'ekip' && (
        <div className="mb-3 space-y-2 text-sm">
          <p className="text-xs text-wait">
            Kişi sayısı buradan türetilir — adam-saat özeti (K7) bu listeye bakar.
            Tikin sahibi <strong>değişmez</strong>; kimin çalıştığı düzeltilir.
          </p>
          <div className="max-h-56 overflow-y-auto rounded-(--radius-mod) border border-line-2 p-2">
            {calisanlar.length === 0 && <p className="text-xs text-wait">Yükleniyor…</p>}
            {calisanlar.map((c) => (
              <label key={c.id} className="flex cursor-pointer items-center gap-2 py-1">
                <input
                  type="checkbox"
                  checked={ekip.includes(c.id)}
                  onChange={(e) =>
                    setEkip(
                      e.target.checked ? [...ekip, c.id] : ekip.filter((x) => x !== c.id),
                    )
                  }
                />
                <span>{c.ad}</span>
                <span className="text-xs text-wait">{c.rol}</span>
              </label>
            ))}
          </div>
          <select
            className={gir}
            value={ekipBasi ?? ''}
            onChange={(e) => setEkipBasi(e.target.value || null)}
          >
            <option value="">Ekip başı seçilmedi</option>
            {calisanlar
              .filter((c) => ekip.includes(c.id))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.ad}
                </option>
              ))}
          </select>
        </div>
      )}

      {adim === 'nihaiOlcu' && (
        <div className="mb-3 space-y-2">
          <p className="text-xs text-wait">
            ⚠️ Nihai ölçü <strong>satırları saklanmıyor</strong>, yalnız toplamı — aşağıdaki
            satırlar keşiften geliyor. Doğru olanları girin, toplam yeniden hesaplanır.
          </p>
          {olculer.map((o, i) => (
            <div key={i} className="flex flex-wrap items-center gap-1.5">
              <input
                className={`${gir} min-w-28 flex-1`}
                value={o.ad}
                placeholder="ad"
                onChange={(e) =>
                  setOlculer(olculer.map((x, j) => (j === i ? { ...x, ad: e.target.value } : x)))
                }
              />
              {(['en', 'boy', 'yuk'] as const).map((alan) => (
                <input
                  key={alan}
                  className={`${gir} num w-20`}
                  inputMode="decimal"
                  value={o[alan]}
                  placeholder={alan}
                  onChange={(e) =>
                    setOlculer(
                      olculer.map((x, j) => (j === i ? { ...x, [alan]: e.target.value } : x)),
                    )
                  }
                />
              ))}
              <button
                onClick={() => setOlculer(olculer.filter((_, j) => j !== i))}
                className="cursor-pointer px-1.5 text-sm text-wait hover:text-crit"
              >
                ✕
              </button>
            </div>
          ))}
          <button
            onClick={() => setOlculer([...olculer, { ad: '', en: '', boy: '', yuk: '' }])}
            className="cursor-pointer text-xs text-navy underline"
          >
            + ölçü satırı
          </button>
          <input
            className={`${gir} num`}
            value={onayNo}
            placeholder="İskele onay no"
            onChange={(e) => setOnayNo(e.target.value)}
          />
        </div>
      )}

      <input
        className={gir}
        value={gerekce}
        placeholder="Düzeltme gerekçesi (zorunlu)"
        onChange={(e) => setGerekce(e.target.value)}
      />

      {hata && <p className="mt-2 text-sm text-crit">{hata}</p>}

      {/*
        ⚠️ DÜĞME SESSİZCE KAPALIYDI. Gerekçe üç karakterden kısaysa kaydet
        sönüyor ama sebebi hiçbir yerde yazmıyordu; kullanıcı "düzenleyemiyorum"
        diyordu (Bedirhan, 04.08.2026). Alanın yanındaki "(zorunlu)" etiketi
        yetmiyor — üç karakter şartı görünmez, "ok" yazan da ölü düğmeyle
        karşılaşıyor. CLAUDE.md'deki ilke: butonu söndür, GEREKÇEYİ GÖSTER.
      */}
      {gerekce.trim().length < 3 && (
        <p className="mt-2 text-xs text-warn">
          Kaydetmek için <strong>düzeltme gerekçesi</strong> yazın (en az 3 karakter). Gerekçe iz
          kaydına işlenir — “eski → yeni” değişikliğinin neden yapıldığı sonradan sorulabilir.
        </p>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          onClick={() => void kaydet()}
          disabled={mesgul || gerekce.trim().length < 3}
          className="rounded-(--radius-mod) bg-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          {mesgul ? 'Kaydediliyor…' : 'Düzeltmeyi kaydet'}
        </button>
        <SessizDugme onClick={onKapat}>Vazgeç</SessizDugme>
      </div>
    </div>
  );
}
